import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {QuotationControllerService, RfqDto} from '../../../../@api';
import {ProgressIndicatorService} from '../../../../@shared';
import {MessagingHandlerService} from '../../../../common/response-messaging-handler/messaging-handler.service';

const DELAY: number = 1000;

@Component({
    selector: 'secure-rfq',
    templateUrl: './rfq.component.html',
    styleUrls: ['./rfq.component.scss']
})
export class RfqComponent implements OnInit {

    number!: string;
    rfq!: RfqDto;
    cols: any[] = [];
    initialised!: boolean;
    statusOptions: any[] = [
        {label: 'Created', value: 'CREATED'},
        {label: 'Pending', value: 'PENDING'},
        {label: 'Verified', value: 'VERIFIED'},
        {label: 'Complete', value: 'COMPLETE'}
    ];
    editable: boolean = false;
    importanceOptions: any[] = [
        {label: 'Reduce electricity bills', value: 'Reduce electricity bills'},
        {label: 'Quick return on investment', value: 'Quick return on investment'},
        {label: 'Quality', value: 'Quality'},
        {label: 'Low upfront costs', value: 'Low upfront costs'},
        {label: 'Ability to upgrade later', value: 'Ability to upgrade later'},
        {label: 'Overcome power interruptions', value: 'Overcome power interruptions'}
    ];
    contactOptions: any[] = [
        {label: 'Morning', value: 'Morning'},
        {label: 'Afternoon', value: 'Afternoon'},
        {label: 'Evening', value: 'Evening'},
        {label: 'Weekends', value: 'Weekends'},
        {label: 'Anytime', value: 'Anytime'},
    ];
    storeyOptions: any[] = [
        {label: 'Single', value: 'Single'},
        {label: 'Multi-Storey', value: 'Multi'}
    ];
    roofTypeOptions: any[] = [
        {label: 'Corrugated', value: 'Corrugated'},
        {label: 'Tile', value: 'Tile'},
        {label: 'Thatch', value: 'Thatch'},
        {label: 'Other', value: 'Other'}
    ];
    roofDirectionOptions: any[] = [
        {label: 'North', value: 'North'},
        {label: 'East', value: 'East'},
        {label: 'South', value: 'South'},
        {label: 'West', value: 'West'}
    ];
    panelsToInverterOptions: any[] = [
        {label: '< 10m', value: 10},
        {label: '< 20m', value: 20},
        {label: '< 30m', value: 30},
        {label: '< 40m', value: 40},
        {label: '40+ m', value: 50}
    ];
    inverterToDbOptions: any[] = [
        {label: '< 10m', value: 10},
        {label: '< 20m', value: 20},
        {label: '< 30m', value: 30},
        {label: '< 40m', value: 40},
        {label: '40+ m', value: 50}
    ];
    dueDate?: Date;

    constructor(private route: ActivatedRoute,
                private quotationService: QuotationControllerService,
                private progress: ProgressIndicatorService,
                private messageService: MessagingHandlerService) {
        this.showProgress();
    }
    
    ngOnInit(): void {
        this.route.url.subscribe((): void => {
            this.number = this.route.snapshot.paramMap.get('number') || '';

            this.quotationService.findRfqByNumber(this.number).subscribe((response: RfqDto): void => {
                this.initRfq(response);
                this.hideProgress(DELAY);
                this.ngModelChanged();
            }, error => {
                this.messageService.showError('Could not load RFQ by number: ' + this.number, 'Request Failed');
                console.log(error);
                this.hideProgress(DELAY);
            });
        });
    }

    initRfq(response: RfqDto): void {
        this.dueDate = new Date(response.dueDate);
        response.dueDate = this.dueDate;

        this.rfq = response;
    }

    /** TAKE NOTE: Multiplier can be +1 or -1 depending on whether you want to
     *  adjust the date GMT+2 or GMT-2 (timezoneOffset *= multiplier) */
    private adjustDate(date: Date, multiplier: number): Date {
        if (date) {
            date = new Date(date);
            let now: Date = new Date();
            let timezoneOffset: number = now.getTimezoneOffset();
            timezoneOffset = timezoneOffset *= multiplier;
            return new Date(date.getTime() + (timezoneOffset * 60000));
        }
        return date;
    }

    showProgress(): void {
        this.progress.publish(true);
    }

    hideProgress(timeout: number = 100): void {
        setTimeout((): void => {
            this.progress.publish(false);
            if (!this.initialised) {
                this.initialised = true;
            }
        }, timeout);
    }

    ngModelChanged(event?: any): void {
        this.editable = (this.rfq?.status !== 'COMPLETE');
    }

    submit(): void {
        this.showProgress();
        let rfq: RfqDto = this.prepareRequestDates();
        this.quotationService.updateRfq(rfq).subscribe((rfq: RfqDto): void => {
            this.rfq.status = rfq.status;
            this.quotationService.findRfqByNumber(this.number).subscribe((response: RfqDto): void => {
                this.rfq = response;
                setTimeout((): void => {
                    this.messageService.showSuccess('Rfq updated successfully', this.rfq.number);
                    this.hideProgress();
                }, DELAY);
            }, error => {
                console.log(error);
                this.hideProgress(DELAY);
                this.messageService.showError('Could not load updated rfq', this.rfq.number);
            });
        }, error => {
            console.log(JSON.stringify(error));
            this.hideProgress(DELAY);
            this.messageService.showError('Update failed', this.rfq.number);
        });
    }

    prepareRequestDates(): RfqDto {
        let dueDate = JSON.parse(JSON.stringify(this.dueDate));
        this.rfq.dueDate = (this.rfq.dueDate === this.dueDate)
            ? this.adjustDate(this.rfq.dueDate, -1) : this.adjustDate(dueDate, -1);

        return this.rfq;
    }

    set(dto: any, member: string, value: any): void {
        if (value) {
            if (value['isTrusted']) {
                dto[member] = +value.target?.value;
            } else {
                dto[member] = value;
            }
        } else {
            dto[member] = value;
        }
        this.calculateTotals();
    }

    calculateTotals(): void {
        let dayUsage: number = this.rfq.dayUsage;
        let nightUsage: number = this.rfq.nightUsage;
        let usage: number = dayUsage + nightUsage;

        let total: number = usage || 0;
        let storage: number = nightUsage || 0;
        let daytime: number = total - storage;
        let day: number = (total > 0) ? this.roundTo((daytime / total * 100), 0) : 0;
        let night: number = this.roundTo((100 - day), 0);

        this.rfq.day = day;
        this.rfq.night = night;
    }

    roundTo(n, digits) {
        let negative: boolean = false;
        if (digits === undefined) {
            digits = 0;
        }
        if (n < 0) {
            negative = true;
            n = n * -1;
        }
        let multiplier: number = Math.pow(10, digits);
        n = parseFloat((n * multiplier).toFixed(11));
        n = (Math.round(n) / multiplier).toFixed(digits);
        if (negative) {
            n = (n * -1).toFixed(digits);
        }
        return n;
    }
}
